import {
  Check,
  ArrowRight,
  MessageCircle,
  ShieldCheck,
  Wrench,
  Headphones,
  Sparkles,
  Home,
  Zap,
  Mic,
  Smartphone,
  CalendarClock,
  CreditCard,
} from "lucide-react";
import { SMART_HOME_KITS, EMI_PARTNERS, PACK_FEATURES, EXPANSION_ITEMS, waLink } from "../data/valuePacks";
import KitCard from "../components/kits/KitCard";
import { useSEO } from "../lib/seo";
import { useCmsSeo } from "../lib/cmsSeo";
import { usePageCms } from "../cms/usePageCms";
import { BRAND } from "../data/content";

/**
 * ValuePacksPage — Smart Home Kits: Smart Value Pack (from ₹39,999), Luxury
 * Smart Kit (from ₹89,999) and Security Kit (₹44,999). Apple/Tesla dark luxury aesthetic.
 *
 * Sections:
 *   Hero -> Certified Hardware -> Kit Cards -> Expand Later -> Included -> EMI
 *   -> Process Timeline -> FAQ -> Final CTA
 */
export default function ValuePacksPage() {
  const seo = useCmsSeo("value-packs", {
    title: "Smart Home Kits · From ₹39,999 — Value Pack, Luxury Kit & Security Kit",
    description:
      "Standardised Smart Home Kits for Indian homes — Smart Value Pack, Luxury Smart Kit and Security Kit. GenieX-engineered hardware, installed and supported end-to-end. No-cost EMI via Bajaj Finserv, Razorpay & Pine Labs.",
    path: "/value-packs",
  });
  useSEO(seo);
  const { data: pageCms } = usePageCms("value-packs");
  const hero = pageCms.hero || {};
  const cmsPacks = Array.isArray(pageCms.packs?.packs) ? pageCms.packs.packs : [];

  return (
    <main data-testid="value-packs-page" className="bg-[var(--gx-bg)] text-white">
      <ValuePacksHero cms={hero} />
      <CertifiedHardwareStrip />
      <WhatsInsideSection />
      <PackGrid cmsPacks={cmsPacks} />
      <ExpandLaterSection />
      <IncludedSection />
      <EMISection />
      <ProcessSection />
      <FAQSection />
      <FinalCTA />
    </main>
  );
}

/* -------------------------------------------------------------------------- */
/* HERO                                                                       */
/* -------------------------------------------------------------------------- */
function ValuePacksHero({ cms = {} }) {
  if (cms.visible === false) return null;
  return (
    <section
      data-testid="vp-hero"
      className="relative min-h-[78svh] w-full overflow-hidden bg-[var(--gx-black)] grain"
    >
      <div className="absolute inset-0">
        <img
          src={cms.desktop_image || "/products/value-pack-still.jpg"}
          alt=""
          className="w-full h-full object-cover opacity-[0.35]"
        />
        <div className="absolute inset-0 vignette" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-[var(--gx-bg)]" />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-5 lg:px-10 pt-32 lg:pt-44 pb-16">
        <p className="eyebrow mb-5">{cms.eyebrow || "Smart Home Kits"}</p>
        <h1 className="h1-display text-white max-w-[18ch]">
          {cms.heading_line1 || "A smart home,"}<br />
          <span className="text-gold">{cms.heading_accent || "starting at ₹39,999."}</span>
        </h1>
        <p className="lead mt-6 lg:mt-8 max-w-[58ch]">
          {cms.description || (
            <>
              Three ready-made kits — Smart Value Pack, Luxury Smart Kit and Security Kit.
              <span className="text-white"> No custom consultation needed.</span>{" "}
              Start with what you need. Add more whenever you're ready.
            </>
          )}
        </p>

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <a
            href={cms.primary_cta_link || "#vp-packs"}
            data-testid="vp-hero-cta-explore"
            className="btn-gold inline-flex items-center gap-2"
          >
            {cms.primary_cta_label || "Explore Kits"} <ArrowRight className="w-3.5 h-3.5" />
          </a>
          <a
            href={cms.secondary_cta_link || waLink(
              "Hi GenieX, I'd like to know more about the Smart Home Kits.",
            )}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="vp-hero-cta-whatsapp"
            className="btn-outline inline-flex items-center gap-2"
          >
            <MessageCircle className="w-3.5 h-3.5" /> {cms.secondary_cta_label || "WhatsApp us"}
          </a>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 lg:gap-6 mt-14 max-w-[920px]">
          {[
            { k: "3", v: "Ready-made kits" },
            { k: "No-Cost", v: "EMI options" },
            { k: "2-Year", v: "Warranty" },
            { k: "Expandable", v: "Add more later" },
          ].map((s) => (
            <div
              key={s.k}
              data-testid={`vp-stat-${s.k.toLowerCase()}`}
              className="rounded-2xl border border-line bg-white/[0.02] backdrop-blur-md p-4 lg:p-5"
            >
              <p className="font-display text-2xl lg:text-3xl text-gold tracking-tight">{s.k}</p>
              <p className="text-[12px] text-dim mt-1.5 tracking-wide">{s.v}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* CERTIFIED HARDWARE STRIP                                                   */
/* -------------------------------------------------------------------------- */
function CertifiedHardwareStrip() {
  return (
    <section
      data-testid="vp-hardware-strip"
      className="relative bg-[var(--gx-bg)] border-y border-line/60 py-10 lg:py-14"
    >
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
        <div className="rounded-[20px] border border-line bg-[var(--gx-surface)]/40 p-6 lg:p-8 flex flex-col sm:flex-row items-center gap-6 lg:gap-10">
          <div
            data-testid="vp-hardware-badge"
            className="w-20 h-20 rounded-2xl border border-gold/40 bg-white/[0.03] flex items-center justify-center shrink-0"
          >
            <ShieldCheck className="w-9 h-9 text-gold" />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <p className="text-[10px] tracking-[0.32em] uppercase text-mute">Hardware Engineered By</p>
            <h3 className="font-display text-2xl lg:text-3xl text-white tracking-tight mt-2">
              GenieX Certified Hardware
            </h3>
            <p className="text-[13.5px] text-dim mt-2 max-w-[62ch]">
              Every Value Pack ships with switches, sensors and gateways engineered
              and tested for Indian voltage and climate. GenieX designs the
              experience, installs the system, and stands behind the service.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* WHAT'S INSIDE — real hardware, unboxed                                    */
/* -------------------------------------------------------------------------- */
function WhatsInsideSection() {
  return (
    <section
      data-testid="vp-whats-inside"
      className="relative bg-[var(--gx-bg)] py-24 lg:py-32 border-t border-line/60 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div>
          <p className="eyebrow mb-5">Real Hardware. Unboxed.</p>
          <h2 className="h1-display text-white">
            Every pack ships in <span className="text-gold">one box.</span>
          </h2>
          <p className="lead mt-7 max-w-[50ch]">
            No loose parts, no third-party gadgets ordered separately. Every switch,
            sensor, doorbell and lock in your pack arrives GenieX-branded, tested
            together, and ready for our engineers to install in a single visit.
          </p>
          <div className="mt-8 flex items-center gap-3 text-[12.5px] text-amber-200/80">
            <ShieldCheck className="w-4 h-4" /> Factory-sealed · GenieX quality-checked
          </div>
        </div>
        <div
          data-testid="vp-inside-image"
          className="relative rounded-[28px] overflow-hidden border border-line bg-[var(--gx-surface)]"
        >
          <img
            src="/products/value-pack-2bhk-contents.jpg"
            alt="Contents of a GenieX Smart Value Pack — doorbell, lock, sensors, plugs and hub"
            className="w-full h-full object-contain p-4"
          />
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* KIT GRID — Smart Value Pack / Luxury Smart Kit / Security Kit             */
/* -------------------------------------------------------------------------- */
function PackGrid({ cmsPacks = [] }) {
  const kits = cmsPacks.length > 0
    ? cmsPacks.filter((p) => p.visible !== false).map((p) => ({
        key: p.key,
        eyebrow: p.badge,
        name: p.title,
        tagline: p.sub,
        description: p.sub,
        bestFor: p.sub,
        highlight: p.highlight,
        canExpand: true,
        variants: [{
          key: p.key,
          label: p.rooms || "All Homes",
          price: 0,
          priceLabel: p.price_label,
          mrpLabel: p.original_price_label,
          coverage: p.coverage || [],
        }],
      }))
    : SMART_HOME_KITS;
  return (
    <section
      id="vp-packs"
      data-testid="vp-pack-grid"
      className="relative bg-[var(--gx-black)] py-24 lg:py-32 border-t border-line/60 grain"
    >
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
        <div className="max-w-[760px] mb-14 lg:mb-20">
          <p className="eyebrow mb-5">Choose Your Kit</p>
          <h2 className="h1-display text-white">
            Three kits. <span className="text-gold">One promise.</span>
          </h2>
          <p className="lead mt-7 max-w-[54ch]">
            Transparent pricing. No &ldquo;starting from&rdquo; asterisks. What you see is what
            you pay — including installation and a 2-year warranty.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {kits.map((kit, i) => (
            <KitCard key={kit.key || i} kit={kit} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* EXPAND LATER — the expansion promise                                      */
/* -------------------------------------------------------------------------- */
function ExpandLaterSection() {
  return (
    <section
      data-testid="vp-expand-later"
      className="relative bg-[var(--gx-bg)] py-24 lg:py-32 border-t border-line/60"
    >
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div>
          <p className="eyebrow mb-5">You don&apos;t have to decide everything today</p>
          <h2 className="h1-display text-white">
            Start small.<br />
            <span className="text-gold">Build bigger, whenever you want.</span>
          </h2>
          <p className="lead mt-7 max-w-[52ch]">
            Every GenieX kit is designed to grow. Buy a Smart Value Pack today, and add
            what you need later — one piece at a time.
          </p>
        </div>
        <div data-testid="vp-expand-items" className="grid sm:grid-cols-2 gap-3">
          {EXPANSION_ITEMS.map((item) => (
            <div
              key={item}
              className="flex items-center gap-3 rounded-xl border border-line bg-[var(--gx-surface)]/40 px-4 py-3.5"
            >
              <Check className="w-4 h-4 text-gold shrink-0" />
              <span className="text-[13.5px] text-white/90">{item}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* INCLUDED — feature highlights                                              */
/* -------------------------------------------------------------------------- */
function IncludedSection() {
  // Strict capability list, sourced from /data/valuePacks.js — no inventions.
  const iconMap = {
    lights: Zap,
    remote: Smartphone,
    motion: Sparkles,
    geyser: ShieldCheck,
    doorbell: Mic,
    lock: Home,
  };
  const features = PACK_FEATURES.map((f) => ({ ...f, icon: iconMap[f.iconKey] || Zap }));

  return (
    <section
      data-testid="vp-features"
      className="relative bg-[var(--gx-bg)] py-24 lg:py-32 border-t border-line/60"
    >
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
        <div className="max-w-[760px] mb-14 lg:mb-20">
          <p className="eyebrow mb-5">Value Pack &amp; Luxury Kit Capabilities</p>
          <h2 className="h1-display text-white">
            The full GenieX experience.<br />
            <span className="text-gold">No compromises.</span>
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-7">
          {features.map((f) => (
            <div
              key={f.title}
              data-testid={`vp-feature-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="card-lux p-7 lg:p-8"
            >
              <div className="w-11 h-11 rounded-xl border border-gold/30 bg-gold/5 flex items-center justify-center text-gold">
                <f.icon className="w-5 h-5" />
              </div>
              <h3 className="font-display text-xl lg:text-2xl text-white mt-5 tracking-tight">
                {f.title}
              </h3>
              <p className="text-[13.5px] text-dim leading-relaxed mt-3">{f.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* EMI                                                                        */
/* -------------------------------------------------------------------------- */
function EMISection() {
  return (
    <section
      data-testid="vp-emi"
      className="relative bg-[var(--gx-black)] py-24 lg:py-32 border-t border-line/60 grain"
    >
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
        <div className="grid lg:grid-cols-[1fr_1fr] gap-12 lg:gap-20 items-start">
          <div>
            <p className="eyebrow mb-5">Pay your way</p>
            <h2 className="h1-display text-white">
              Own it today.<br />
              <span className="text-gold">Pay over time.</span>
            </h2>
            <p className="lead mt-7 max-w-[52ch]">
              Three financing partners — choose what&apos;s most convenient. Approval at
              your doorstep, on your phone, or on any major card.
            </p>
            <div className="mt-8 inline-flex items-center gap-3 text-[12.5px] text-amber-200/80">
              <CreditCard className="w-4 h-4" /> EMI starts as low as{" "}
              <span className="text-white">₹2,083 / month</span>
            </div>
          </div>

          <div className="space-y-4">
            {EMI_PARTNERS.map((p) => (
              <div
                key={p.key}
                data-testid={`vp-emi-${p.key}`}
                className="card-lux p-5 lg:p-6 flex items-center gap-5"
              >
                <div
                  className={`shrink-0 w-[120px] h-[60px] rounded-xl flex items-center justify-center ${
                    p.bg === "white"
                      ? "bg-white shadow-[0_8px_20px_-8px_rgba(0,0,0,0.4)]"
                      : "border border-line bg-white/[0.03]"
                  }`}
                >
                  {p.logo ? (
                    <img src={p.logo} alt={p.name} className="max-h-[44px] max-w-[100px] object-contain" />
                  ) : (
                    <span className="font-display text-[15px] tracking-tight text-white">{p.name}</span>
                  )}
                </div>
                <div className="flex-1">
                  <p className="font-display text-lg text-white tracking-tight">{p.name}</p>
                  <p className="text-[13px] text-dim mt-1">{p.blurb}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* PROCESS                                                                    */
/* -------------------------------------------------------------------------- */
function ProcessSection() {
  const steps = [
    { n: "01", t: "WhatsApp us", d: "Send a quick message. We confirm pack, address and visit slot." },
    { n: "02", t: "Free site survey", d: "GenieX engineer measures your home, confirms scope — no obligation." },
    { n: "03", t: "Pay & schedule", d: "Choose UPI, card or no-cost EMI. We lock the installation date." },
    { n: "04", t: "1-day install", d: "Our crew installs, configures, and trains your family — all in one day." },
  ];

  return (
    <section
      data-testid="vp-process"
      className="relative bg-[var(--gx-bg)] py-24 lg:py-32 border-t border-line/60"
    >
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
        <div className="max-w-[760px] mb-14 lg:mb-20">
          <p className="eyebrow mb-5">How it works</p>
          <h2 className="h1-display text-white">
            From WhatsApp<br />
            <span className="text-gold">to a smart home — in a week.</span>
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-7">
          {steps.map((s) => (
            <div
              key={s.n}
              data-testid={`vp-step-${s.n}`}
              className="rounded-2xl border border-line bg-[var(--gx-surface)]/40 p-6 lg:p-7"
            >
              <p className="font-display text-3xl text-gold/90 tracking-tight">{s.n}</p>
              <h3 className="font-display text-xl text-white mt-5 tracking-tight">{s.t}</h3>
              <p className="text-[13px] text-dim leading-relaxed mt-2">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* FAQ                                                                         */
/* -------------------------------------------------------------------------- */
function FAQSection() {
  const faqs = [
    {
      q: "What hardware do the Smart Home Kits use?",
      a: "In-house GenieX-engineered switches, sensors and gateways — tested for Indian voltage and climate. We design the experience, install the system, and support it end-to-end.",
    },
    {
      q: "I already live in my home. Can I still get a kit?",
      a: "Yes. Every kit is designed to retrofit behind your existing switches and wiring — no rewiring, no civil work, minimal disruption.",
    },
    {
      q: "Do I need to decide everything on day one?",
      a: "No. Start with the kit that covers what matters most today. Curtains, cameras, sensors, a robot vacuum and more can all be added later, one piece at a time.",
    },
    {
      q: "What if my home has more rooms or more switches than the kit covers?",
      a: "Our engineer surveys your home for free. If you need additional switches, we'll quote them transparently — no hidden charges.",
    },
    {
      q: "What's covered under warranty?",
      a: "2 years on every kit. If a product develops a fault, we repair it free of cost. If it genuinely can't be repaired, we replace it — as per warranty terms.",
    },
    {
      q: "Which cities do you serve?",
      a: "Hyderabad, Vijayawada and major cities across Telangana & Andhra Pradesh today. For other cities in India, please WhatsApp us — we travel for projects.",
    },
    {
      q: "Is the EMI really no-cost?",
      a: "Yes. With Bajaj Finserv, Razorpay or Pine Labs we offer 0% interest EMI on tenures of 3–24 months, subject to your card/lender approval.",
    },
  ];

  return (
    <section
      data-testid="vp-faq"
      className="relative bg-[var(--gx-black)] py-24 lg:py-32 border-t border-line/60 grain"
    >
      <div className="max-w-[1100px] mx-auto px-5 lg:px-10">
        <div className="mb-14 lg:mb-20">
          <p className="eyebrow mb-5">Frequently asked</p>
          <h2 className="h1-display text-white">
            Honest answers.<br />
            <span className="text-gold">No fine print.</span>
          </h2>
        </div>
        <div className="space-y-4">
          {faqs.map((f, i) => (
            <details
              key={i}
              data-testid={`vp-faq-${i}`}
              className="group rounded-2xl border border-line bg-[var(--gx-surface)]/40 p-6 lg:p-7 open:border-gold/40 transition-colors"
            >
              <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                <span className="font-display text-lg lg:text-xl text-white tracking-tight">{f.q}</span>
                <span className="text-gold mt-1 transition-transform group-open:rotate-45 text-2xl leading-none">+</span>
              </summary>
              <p className="text-[14px] text-dim leading-relaxed mt-4">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* FINAL CTA                                                                  */
/* -------------------------------------------------------------------------- */
function FinalCTA() {
  return (
    <section
      data-testid="vp-final-cta"
      className="relative bg-[var(--gx-bg)] py-24 lg:py-32 border-t border-line/60"
    >
      <div className="max-w-[1100px] mx-auto px-5 lg:px-10 text-center">
        <Sparkles className="w-7 h-7 text-gold mx-auto mb-5" />
        <h2 className="h1-display text-white">
          Your smart home is one<br />
          <span className="text-gold">WhatsApp away.</span>
        </h2>
        <p className="lead mt-7 max-w-[58ch] mx-auto">
          Send us a message. Get pack details, free site visit slots, and EMI options —
          all on WhatsApp, no forms required.
        </p>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <a
            href={waLink(
              "Hi GenieX, I'd like to know more about the Smart Home Value Packs.",
            )}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="vp-final-whatsapp"
            className="btn-gold inline-flex items-center gap-2"
          >
            <MessageCircle className="w-3.5 h-3.5" /> WhatsApp GenieX
          </a>
          <a
            href={`tel:${BRAND.phoneRaw}`}
            data-testid="vp-final-call"
            className="btn-outline inline-flex items-center gap-2"
          >
            <CalendarClock className="w-3.5 h-3.5" /> Call {BRAND.phone}
          </a>
        </div>
        <p className="text-[12px] text-mute mt-8">
          GenieX-engineered hardware. Designed, installed and serviced by GenieX.
        </p>
      </div>
    </section>
  );
}
